import { dataSourcesSelection, IDataSourceApp } from "./DatasetsSources.config";
import { Divider } from "antd";
import styled from "styled-components";
import { MutableRefObject, useState } from "react";
import { isEmpty, Authority } from "@dataone/lib";
import { useAuth } from "../../../../auth/contexts/auth.provider";
import { ArrowDownIcon, CaretRightIcon } from "@dataone/icons";

interface DatasetsSourcesProps {
  stepRef2?: MutableRefObject<null>;
  stepRef3?: MutableRefObject<null>;
  filteredDatasets: IDataSourceApp[];

  setFilteredDatasets: (filteredDatasets: IDataSourceApp[]) => void;
  setDataSource: (dataSource: IDataSourceApp) => void;
  setCurrent: (current: number) => void;
}

const DatasetsSources = ({
  stepRef2,
  stepRef3,
  filteredDatasets,
  setFilteredDatasets,
  setDataSource,
  setCurrent,
}: DatasetsSourcesProps) => {
  const { user } = useAuth();

  const hasDatabases =
    ((user?.groups[0] === Authority.OG_SUBSCRIBER ||
      user?.groups[0] === Authority.OG_SUBSCRIBER_EX) &&
      user?.subscription.hasDatabases === true) ||
    user?.groups[0] === Authority.OG_ADMIN;
  const hasFiles =
    ((user?.groups[0] === Authority.OG_SUBSCRIBER ||
      user?.groups[0] === Authority.OG_SUBSCRIBER_EX) &&
      user?.subscription.hasFiles === true) ||
    user?.groups[0] === Authority.OG_ADMIN;
  const hasEnterpriseConnectors =
    ((user?.groups[0] === Authority.OG_SUBSCRIBER ||
      user?.groups[0] === Authority.OG_SUBSCRIBER_EX) &&
      user?.subscription.hasEnterpriseConnectors === true) ||
    user?.groups[0] === Authority.OG_ADMIN;
  const hasCloudStorage =
    ((user?.groups[0] === Authority.OG_SUBSCRIBER ||
      user?.groups[0] === Authority.OG_SUBSCRIBER_EX) &&
      user?.subscription.hasCloudStorage === true) ||
    user?.groups[0] === Authority.OG_ADMIN;

  const filteredDataSources = dataSourcesSelection.filter((category) => {
    if (category.key === "databases" && !hasDatabases) {
      return false;
    }
    if (category.key === "files" && !hasFiles) {
      return false;
    }
    if (category.key === "entreprise-connectors" && !hasEnterpriseConnectors) {
      return false;
    }
    if (category.key === "cloud-storages" && !hasCloudStorage) {
      return false;
    }
    return true;
  });

  const [collapsedDatasetKey, setCollapsedDatasetKey] = useState<string | null>(
    null
  );

  const handleDatasetClick = (key: string) => {
    if (collapsedDatasetKey === key) setCollapsedDatasetKey(null);
    else setCollapsedDatasetKey(key);
  };

  const handleDataset = (key: number, app: IDataSourceApp) => {
    setDataSource(app);
    setCurrent(key);
    setFilteredDatasets([]);
  };

  return (
    <>
      <StyledCollaps>
        {isEmpty(filteredDatasets)
          ? filteredDataSources.map((dataset, index) => (
              <div key={dataset.key}>
                <StyledType
                  {...(index === 0 && { ref: stepRef2 })}
                  onClick={() => handleDatasetClick(dataset.key)}
                  disabled={index > 4}
                >
                  <FirstDiv>
                    <StyledIcon>{dataset.icon}</StyledIcon>
                    <div className="name"> {dataset.label}</div>
                  </FirstDiv>
                  {
                    <StyledCaretRightIcon
                      collapsed={
                        collapsedDatasetKey === dataset.key ? true : false
                      }
                    >
                      <CaretRightIcon />
                    </StyledCaretRightIcon>
                  }
                </StyledType>
                {collapsedDatasetKey === dataset.key && (
                  <div>
                    {dataset.apps.map((app, appIndex) => (
                      <SubType key={app.title}>
                        <StyledDiv
                          key={appIndex}
                          {...(index === 0 &&
                            appIndex === 0 && { ref: stepRef3 })}
                          onClick={() => handleDataset(1, app)}
                        >
                          <FirstDiv>
                            <div className="apps-icon">{app.icon}</div>
                            <div className="name title">{app.title}</div>
                          </FirstDiv>
                          <StyledArrowDownIcon>
                            <ArrowDownIcon />
                          </StyledArrowDownIcon>
                        </StyledDiv>
                      </SubType>
                    ))}
                  </div>
                )}
                <Divider />
              </div>
            ))
          : filteredDatasets.map((app, appIndex) => (
              <SubType>
                <StyledDiv key={appIndex} onClick={() => handleDataset(1, app)}>
                  <FirstDiv>
                    <div className="apps-icon">{app.icon}</div>
                    <div className="name title">{app.title}</div>
                  </FirstDiv>
                  <div className="icon">
                    <CaretRightIcon />
                  </div>
                </StyledDiv>
              </SubType>
            ))}
      </StyledCollaps>
    </>
  );
};

export default DatasetsSources;

interface StyledTypeProps {
  disabled?: boolean;
}

const StyledType = styled.div<StyledTypeProps>`
  color: #040726;
  font-size: 24px;
  font-weight: 500;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  cursor: pointer;

  ${({ disabled }) =>
    disabled &&
    `
opacity: 0.5; /* Apply opacity for disabled elements */
pointer-events: none; /* Disable click events for disabled elements */

`}
`;

const StyledCollaps = styled.div`
  margin: 25px;
  .ant-divider-horizontal {
    border: 1.5px solid #eff1fd;
    margin-top: 16px;
    margin-bottom: 16px;
  }
`;

const FirstDiv = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  .name {
    margin-left: 20px;
    // margin-bottom: 10px;
    font-size: 16px;
    color: #040726;
    font-weight: 500;
  }
  .title {
    font-size: 13px;
  }
  .icon img {
    width: 35px;
  }
  .apps-icon {
    min-width: 40px;
    font-size: 25px;
    display: flex;
    img {
      width: 25px;
      height: 25px;
    }
  }
`;

const StyledDiv = styled.div`
  color: #040726;
  // font-size: 20px;
  font-weight: 400;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  .icon {
    margin-left: auto;
  }
  .icon img {
    width: 18px;
  }
`;

const SubType = styled.div`
  margin: 20px 0px 10px 65px;
`;

const StyledIcon = styled.div`
  padding: 10px;
  border-radius: 50%;
  background: #eff1fd;
  // width: 35px;
  color: #4974f6;
  font-size: 18px;
  display: flex;
`;

const StyledArrowDownIcon = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  color: #000;
  font-size: 8px;
  transform: rotate(270deg);
`;

const StyledCaretRightIcon = styled.div<{ collapsed: boolean }>`
  margin-left: auto;
  font-size: 8px;
  display: flex;
  transform: ${(props) =>
    props.collapsed ? "rotate(90deg)" : "rotate(-90deg)"};
  border-radius: 50%;
  border: 1px solid #040726;
  padding: 2px;
`;
